import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ContactsService } from '@src/api';
import { BaseResizableComponent } from '@src/app/modules/base';
import { AuthUserService } from '@src/app/modules/auth';
import { AlertService } from '@src/core/services';
import { ContactUI } from '@src/models';

import { FormDataControlsType, FormDataType } from './types';

@Component({
  selector: 'app-standard-user-info-fields',
  templateUrl: './standard-user-info-fields.component.html',
  styleUrls: ['./standard-user-info-fields.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StandardUserInfoFieldsComponent extends BaseResizableComponent {
  private readonly contactsService = inject(ContactsService);
  private readonly authUserService = inject(AuthUserService);
  private readonly alertService = inject(AlertService);
  private readonly translateService = inject(TranslateService);

  loading = false;
  form!: FormGroup<FormDataControlsType>;

  get parentOrganisationId() {
    return this.authUserService.user?.parentOrganisationId;
  }

  get contactsControl() {
    return this.form.controls.contacts;
  }

  constructor() {
    super();

    this.initForm();
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.getStandardContacts();
    this.cdr.markForCheck();
  }

  onSave() {
    this.loading = true;
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      this.alertService.error(this.translateService.instant('common.alerts.errors.fillRequired'));
      this.loading = false;
      return;
    }

    const fields = this.mapFormDataToContactUI(this.form.value);

    this.contactsService
      .addOrUpdateUserContactsForUnion(fields)
      .pipe(takeUntil(this.destroyed$$))
      .subscribe(() => {
        this.loading = false;
        this.alertService.success(this.translateService.instant('common.alerts.successes.saveData'));

        this.getStandardContacts();
        this.cdr.markForCheck();
      });

    this.cdr.markForCheck();
  }

  private getStandardContacts() {
    if (this.parentOrganisationId) {
      this.loading = true;
      this.contactsService
        .getUserContactsForUnion(this.parentOrganisationId)
        .pipe(takeUntil(this.destroyed$$))
        .subscribe(fields => {
          const formData = this.mapContactUIToFormData(fields);
          this.form.patchValue(formData);
          this.loading = false;

          this.cdr.markForCheck();
        });
    }
  }

  private initForm(): void {
    this.form = new FormGroup<FormDataControlsType>({
      contacts: new FormControl<ContactUI[]>([], {
        nonNullable: true,
      }),
    });
  }

  private mapContactUIToFormData(contacts: ContactUI[]): FormDataType {
    contacts?.map(contact => {
      contact.description = contact.description?.trim();
      return contact;
    });

    return <FormDataType>{
      contacts: contacts ?? [],
    };
  }

  private mapFormDataToContactUI(formData: FormDataType): ContactUI[] | undefined {
    const contacts = formData.contacts?.map(contact => {
      delete contact.contactListValues;

      contact.description = contact.description?.trim();
      return contact;
    });

    return contacts;
  }
}
